<template>
  <div class="form-page-container">
    <PremiumPage
      title="Create New Event"
      subtitle="Organize society gatherings and celebrations"
      showBack
    >
      <div class="pa-4">
        <v-form>
          <div class="half-width-wrap">
            <!-- ── Image Upload Section ── -->
            <div class="mb-6">
              <div class="premium-field-label mb-3">Banner Image</div>
              <div class="banner-upload-box" @click="openFileExplorer">
                <input
                  type="file"
                  name="profile_pic"
                  @change="changeFile($event)"
                  ref="profile_pic"
                  hidden
                />
                <v-img
                  v-if="previewImage || event.picture_url"
                  :src="
                    previewImage ||
                    API_BASE_URL + '/uploads/' + event.picture_url
                  "
                  class="banner-preview"
                  aspect-ratio="2.5"
                >
                  <div class="change-overlay">
                    <v-icon color="white">mdi-camera-outline</v-icon>
                    <span>Change Banner</span>
                  </div>
                </v-img>
                <div v-else class="upload-placeholder">
                  <div class="upload-icon-wrap">
                    <v-icon size="28" color="#94A3B8"
                      >mdi-image-plus-outline</v-icon
                    >
                  </div>
                  <p class="upload-title">Click to upload event banner</p>
                  <span class="upload-hint"
                    >Recommended: 1200×500px · JPG, PNG, GIF</span
                  >
                </div>
              </div>
              <div class="error-text mt-2" v-if="serverSideErrors.picture">
                {{ serverSideErrors.picture }}
              </div>
              <div class="hint-text mt-2" v-else>
                Only jpg, png, gif are allowed
              </div>
            </div>

            <!-- ── Event Title ── -->
            <div class="mb-4">
              <PremiumInput
                v-model="event.title"
                label="Title *"
                placeholder="e.g. Annual General Body Meeting 2024"
                icon="mdi-calendar-star"
                v-validate="'required'"
                name="title"
                :error="errors.first('title') || serverSideErrors.title"
              />
            </div>

            <!-- ── Event Description ── -->
            <div class="mb-4">
              <div class="premium-field-label mb-2">Description *</div>
              <v-textarea
                v-model="event.description"
                name="description"
                placeholder="Details about the event, location, schedule, etc..."
                v-validate="'required'"
                rows="4"
                outlined
                hide-details="auto"
                class="premium-textarea"
                :error="
                  !!errors.first('description') ||
                  !!serverSideErrors.description
                "
                :error-messages="
                  errors.first('description') || serverSideErrors.description
                "
              />
            </div>

            <!-- ── Start Date ── -->
            <div class="mb-4">
              <PremiumDateTimePicker
                v-model="event.start_datetime"
                label="Starts At *"
                placeholder="Select start date & time"
                v-validate="'required'"
                name="StartDate"
                :error="
                  errors.first('StartDate') || serverSideErrors.start_datetime
                "
              />
            </div>

            <!-- ── End Date ── -->
            <div class="mb-2">
              <PremiumDateTimePicker
                v-model="event.end_datetime"
                label="Ends At *"
                placeholder="Select end date & time"
                v-validate="'required'"
                name="endDate"
                :error="
                  errors.first('endDate') || serverSideErrors.end_datetime
                "
              />
            </div>
          </div>
        </v-form>
      </div>

      <template #footer>
        <v-btn text class="btn-premium-cancel" @click="$router.go(-1)"
          >Cancel</v-btn
        >
        <v-btn
          depressed
          :loading="loading"
          class="btn-premium-primary ml-3"
          @click="saveEventDetail"
        >
          Submit
        </v-btn>
      </template>
    </PremiumPage>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import moment from "moment";
import PremiumPage from "../../common/PremiumPage.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumDateTimePicker from "../../common/PremiumDateTimePicker.vue";

export default {
  components: { PremiumPage, PremiumInput, PremiumDateTimePicker },
  data() {
    return {
      user: {},
      loading: false,
      member: {},
      serverSideErrors: {},
      event: {
        title: "",
        description: "",
        start_datetime: new Date(),
        end_datetime: new Date(new Date().setHours(new Date().getHours() + 1)),
        picture: [],
      },
      profile_pic: {},
      previewImage: null,
    };
  },
  watch: {
    "event.start_datetime": function () {
      if (this.event.start_datetime && this.event.end_datetime) {
        let difference = moment(this.event.end_datetime).diff(
          this.event.start_datetime,
          "minutes",
        );
        if (difference < 0) {
          this.event.end_datetime = moment(this.event.start_datetime)
            .add("1", "hour")
            .toDate();
        }
      }
    },
    "event.end_datetime": function () {
      if (this.event.start_datetime && this.event.end_datetime) {
        let difference = moment(this.event.start_datetime).diff(
          this.event.end_datetime,
          "minutes",
        );
        if (difference > 0) {
          this.event.start_datetime = moment(this.event.end_datetime)
            .subtract("1", "hour")
            .toDate();
        }
      }
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    saveEventDetail() {
      let _self = this;
      this.$validator.validateAll().then((result) => {
        if (result) {
          _self.loading = true;
          let formData = new FormData();
          formData.append("title", this.event.title);
          formData.append("description", this.event.description);
          formData.append(
            "start_datetime",
            moment.utc(this.event.start_datetime).valueOf(),
          );
          formData.append(
            "end_datetime",
            moment.utc(this.event.end_datetime).valueOf(),
          );
          formData.append("picture", this.profile_pic);

          _self.$axios
            .post("/societyadmin/events/", formData, {
              headers: { "Content-Type": "multipart/form-data" },
            })
            .then((response) => {
              let data = response.data;
              _self.loading = false;
              if (data.status) {
                this.setAlert({
                  showAlert: true,
                  message: data.message,
                  variant: "green accent-4",
                  timeout: 2000,
                });
                _self.$router.push({ path: "/societyadmin/event" });
              } else {
                if (!data.errors) {
                  this.setAlert({
                    showAlert: true,
                    message: data.message,
                    variant: "red accent-3",
                    timeout: 2000,
                  });
                } else {
                  _self.serverSideErrors = {};
                  for (let i = 0; i < data.errors.length; i++) {
                    this.serverSideErrors[data.errors[i].field] =
                      data.errors[i].message;
                  }
                }
              }
            });
        }
      });
    },
    openFileExplorer() {
      this.$refs.profile_pic.click();
    },
    changeFile(e) {
      this.profile_pic = e.target.files[0];
      let input = this.$refs.profile_pic;
      let file = input.files;
      if (file && file[0]) {
        let reader = new FileReader();
        reader.onload = (e) => {
          this.previewImage = e.target.result;
        };
        reader.readAsDataURL(file[0]);
        this.$emit("input", file[0]);
      }
    },
  },
  mounted() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
};
</script>

<style scoped>
.form-page-container {
  padding: 24px;
}

/* ── 50% width wrapper ── */
.half-width-wrap {
  max-width: 50%;
}
@media (max-width: 960px) {
  .half-width-wrap {
    max-width: 100%;
  }
}

.premium-field-label {
  font-size: 11.5px;
  font-weight: 700;
  color: var(--tx3, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* ── Banner Upload ── */
.banner-upload-box {
  width: 100%;
  border-radius: 14px;
  border: 2px dashed var(--border, #e2e8f0);
  background: var(--surface2, #f8fafc);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.banner-upload-box:hover {
  border-color: var(--brand);
  background: var(--surface);
}

.banner-preview {
  width: 100%;
  position: relative;
}
.change-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: white;
  font-weight: 700;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.2s;
}
.banner-upload-box:hover .change-overlay {
  opacity: 1;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 20px;
  text-align: center;
}
.upload-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
}
.upload-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--tx2, #374151);
  margin: 0;
}
.upload-hint {
  font-size: 11.5px;
  color: var(--tx3, #94a3b8);
}

/* ── Custom Textarea ── */
::v-deep .premium-textarea .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2, #f8fafc) !important;
  border: 1px solid var(--border, #e2e8f0) !important;
}
::v-deep .premium-textarea fieldset {
  border: none;
}
::v-deep .premium-textarea textarea {
  font-size: 13.5px !important;
  color: var(--tx) !important;
  line-height: 1.6 !important;
}
::v-deep .premium-textarea textarea::placeholder {
  color: var(--tx3, #94a3b8) !important;
  font-weight: 400 !important;
}

/* ── DateTime Picker ── */
::v-deep .premium-datetime-picker .v-input__slot {
  border-radius: 12px !important;
  background: var(--surface2, #f8fafc) !important;
  border: 1px solid var(--border, #e2e8f0) !important;
  min-height: 48px !important;
  padding: 0 12px !important;
}
::v-deep .premium-datetime-picker input {
  font-size: 13.5px !important;
  font-weight: 600 !important;
  color: var(--tx) !important;
}
::v-deep .premium-datetime-picker input::placeholder {
  color: var(--tx3, #94a3b8) !important;
  font-weight: 400 !important;
}

.error-text {
  font-size: 12px;
  color: #ef4444;
  font-weight: 500;
}
.hint-text {
  font-size: 12px;
  color: var(--tx3);
}
</style>
